import moment from 'moment';
import type {
  FilterFieldConfig,
  DatasetSearchParams,
  DatasetFacets,
} from '../types';
import {
  fieldToChips,
  fieldParamKeys,
  type FieldChip,
} from '../filter-modal/field-chips';

export interface FilterChipData {
  label: string;
  /** For scalar filters: the param key. For picklist items: `param:value`. */
  key: string;
  /** The payload key to modify when removing */
  paramKey: string;
  /** For picklist chips: the individual value to remove from the array */
  arrayValue?: string;
  /** Further payload keys the chip clears with paramKey (a range shown as one chip) */
  extraParamKeys?: string[];
}

export interface FilterGroup {
  category: string;
  chips: FilterChipData[];
  clearKeys: string[];
  /** True when this group is disabled (excluded from the query but retained). */
  disabled?: boolean;
}

const formatDate = (d: string) => moment(d).format('YYYY-MM-DD');

// Adapt a normalized chip to the sidebar's remove shape (paramKey/arrayValue).
// Ranges split into separate From/To chips with moment-formatted dates.
const toChipData = (c: FieldChip): FilterChipData[] => {
  switch (c.kind) {
    case 'labeled':
      return [{ label: c.label, key: c.key, paramKey: c.param }];
    case 'value':
      return [
        {
          label: `${c.fieldLabel}: ${c.resolved}`,
          key: c.key,
          paramKey: c.param,
          arrayValue: c.rawValue,
        },
      ];
    case 'range': {
      const out: FilterChipData[] = [];
      if (c.min) {
        out.push({
          label:
            c.format === 'date'
              ? `${c.fieldLabel} from ${formatDate(c.min)}`
              : `${c.fieldLabel} >= ${c.min}`,
          key: c.minParam,
          paramKey: c.minParam,
        });
      }
      if (c.max) {
        out.push({
          label:
            c.format === 'date'
              ? `${c.fieldLabel} to ${formatDate(c.max)}`
              : `${c.fieldLabel} <= ${c.max}`,
          key: c.maxParam,
          paramKey: c.maxParam,
        });
      }
      return out;
    }
    case 'value-range':
      return [
        {
          label: c.label,
          key: c.key,
          paramKey: c.minParam,
          extraParamKeys: [c.maxParam],
        },
      ];
  }
};

export function buildFilterGroups(
  payload: DatasetSearchParams,
  filters: FilterFieldConfig[],
  facets?: DatasetFacets | null
): FilterGroup[] {
  // Collect unique categories in config order
  const categoryOrder: string[] = [];
  const seen = new Set<string>();
  for (const f of filters) {
    if (!seen.has(f.category)) {
      seen.add(f.category);
      categoryOrder.push(f.category);
    }
  }

  const result: FilterGroup[] = [];

  for (const category of categoryOrder) {
    const fields = filters.filter((f) => f.category === category);
    const chips: FilterChipData[] = [];
    const clearKeys: string[] = [];

    for (const field of fields) {
      clearKeys.push(...fieldParamKeys(field));
      for (const chip of fieldToChips(field, payload, facets).chips) {
        chips.push(...toChipData(chip));
      }
    }

    if (chips.length > 0) {
      result.push({ category, chips, clearKeys });
    }
  }

  return result;
}

interface FilterMoveResult {
  payload: DatasetSearchParams;
  disabled: DatasetSearchParams;
}

/**
 * Move a group's keys out of the live payload into the disabled stash.
 * @param {string[]} clearKeys keys to move out of the payload
 * @param {DatasetSearchParams} payload the live search params
 * @param {DatasetSearchParams} disabled the disabled stash
 * @return {FilterMoveResult} updated payload and disabled stash
 */
export function disableGroupPayload(
  clearKeys: string[],
  payload: DatasetSearchParams,
  disabled: DatasetSearchParams
): FilterMoveResult {
  const moved: Record<string, unknown> = {};
  const cleared: Record<string, undefined> = {};
  clearKeys.forEach((k) => {
    if (payload[k] !== undefined) moved[k] = payload[k];
    cleared[k] = undefined;
  });
  return {
    disabled: { ...disabled, ...moved },
    payload: { ...payload, page: 1, ...cleared },
  };
}

/**
 * Restore a disabled group's keys back into the live payload.
 * @param {string[]} clearKeys keys to restore into the payload
 * @param {DatasetSearchParams} payload the live search params
 * @param {DatasetSearchParams} disabled the disabled stash
 * @return {FilterMoveResult} updated payload and disabled stash
 */
export function enableGroupPayload(
  clearKeys: string[],
  payload: DatasetSearchParams,
  disabled: DatasetSearchParams
): FilterMoveResult {
  const restored: Record<string, unknown> = {};
  const nextDisabled = { ...disabled };
  clearKeys.forEach((k) => {
    if (disabled[k] !== undefined) restored[k] = disabled[k];
    delete nextDisabled[k];
  });
  return {
    disabled: nextDisabled,
    payload: { ...payload, page: 1, ...restored },
  };
}
